import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { IEbayListingBatchItem } from "../../../models/bulkListings/BulkListing";
import PopOutBox from "../../shared/PopOutBox";
import RecentlyListedTypeCell from "./RecentlyListedTypeCell";
import UnlistedReadyStatus from "../unlistedTable/UnlistedReadyStatus";
import Modal from "../../../layouts/Modal";
import useApiHelper from "../../../hooks/useApiHelper";
import useBatchListingsMutations from "../../../data/useBatchListingsMutations";


export default function RecentlyListedErrorCell({ batchItem, showExtraInfo = true }: { batchItem: IEbayListingBatchItem, showExtraInfo?: boolean }) {
  const [modalOpen, setModalOpen] = useState(false);
  const { put } = useApiHelper();
  const queryClient = useQueryClient();

  const { cancelMutation } = useBatchListingsMutations();

  const isStuck = !batchItem.isListed && !batchItem.isCancelled && !!batchItem.partData?.listingProcessing;

  if (batchItem.isCancelled) {
    return (
      <div className="flex flex-col gap-y-4 items-center justify-center">
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
          Cancelled
        </span>
        {showExtraInfo && <RecentlyListedTypeCell type={batchItem.type} />}
      </div>
    );
  }

  if (isStuck) {
    if (batchItem.isProcessing) {
      return (
        <div className="flex flex-col gap-y-2 items-center justify-center">
          <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700">
            Processing…
          </span>
          {showExtraInfo && <RecentlyListedTypeCell type={batchItem.type} />}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-y-2 items-center justify-center">
        <button
          onClick={(e) => { e.stopPropagation(); cancelMutation.mutate(batchItem.id); }}
          disabled={cancelMutation.isLoading}
          className="inline-flex items-center gap-x-1 rounded px-2 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <XCircleIcon className="h-4 w-4" />
          {cancelMutation.isLoading ? "Cancelling..." : "Cancel Listing"}
        </button>
        {showExtraInfo && <RecentlyListedTypeCell type={batchItem.type} />}
      </div>
    );
  }

  if (!batchItem.errorMessage) {
    return (
      <div className="flex flex-col gap-y-4 items-center justify-center">
        <CheckCircleIcon className="h-6 w-6 text-green-500" />
        <RecentlyListedTypeCell type={batchItem.type} />
      </div>
    )
  }

  const errorIcon = batchItem.isListed
    ? <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 cursor-pointer" />
    : <ExclamationTriangleIcon className="h-6 w-6 text-red-500 cursor-pointer" />;

  return (
    <>
      <div className="flex flex-col gap-y-4 items-center justify-center">
        <div className="hover:scale-110 z-50" onClick={(e) => { e.stopPropagation(); setModalOpen(true); }}>
          <PopOutBox
            position="left"
            classes="max-w-3xl max-w-[60vw] max-h-[50vh] overflow-y-auto"
            icon={errorIcon}
          >
            <div className="p-2">
              <p className="text-sm font-medium">eBay Error - Click to see full error</p>
              <pre>{batchItem.errorMessage}</pre>
            </div>
          </PopOutBox>
        </div>
        {showExtraInfo && (
          <>
            <RecentlyListedTypeCell type={batchItem.type} />
            {!batchItem.isListed && <UnlistedReadyStatus part={batchItem.partData} />}
          </>
        )}

      </div>
      <Modal open={modalOpen} setOpen={setModalOpen} width="max-w-[70vw]">
        <div className="p-4">
          <h3 className="text-lg font-semibold mb-4">eBay Error</h3>
          <div className="max-h-[70vh] overflow-y-auto">
            <pre className="overflow-x-auto break-words text-sm bg-gray-50 p-4 rounded border">{batchItem.errorMessage}</pre>
          </div>
        </div>
      </Modal>
    </>
  )
}